import React, { useEffect, useState } from "react";
import { api, mediaUrl, formatApiError } from "@/lib/api";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { Check, Loader2, UserPlus, Sparkles, Coins, Clock, AlertTriangle, RefreshCw } from "lucide-react";

function EstimateBar({ projectId }) {
  const [est, setEst] = useState(null);
  useEffect(() => {
    api.get(`/projects/${projectId}/estimate`).then((r) => setEst(r.data)).catch(() => {});
  }, [projectId]);
  if (!est) return null;
  const c = est.projected_credits || {};
  const bal = est.balance_credits;
  const short = bal != null && !est.enough_credits;
  return (
    <div className="mb-6 border border-zinc-800 bg-zinc-900/60 rounded-sm p-4" data-testid="estimate-bar">
      <div className="flex items-center gap-2 text-amber-500 mb-3">
        <Coins className="w-4 h-4" />
        <span className="font-mono2 text-[10px] uppercase tracking-[0.2em]">Estimated Usage (before you generate)</span>
      </div>
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
        <div>
          <p className="text-2xl font-display text-white" data-testid="est-clips">{est.clips}</p>
          <p className="font-mono2 text-[9px] uppercase tracking-widest text-zinc-500">clips ({est.clip_seconds}s video)</p>
        </div>
        <div>
          <p className="text-2xl font-display text-white flex items-center gap-1"><Clock className="w-4 h-4 text-zinc-500" />~{est.est_minutes?.total}m</p>
          <p className="font-mono2 text-[9px] uppercase tracking-widest text-zinc-500">est. time</p>
        </div>
        <div>
          <p className="text-2xl font-display text-amber-400" data-testid="est-credits">
            {c.low === c.high ? c.high : `${c.low}–${c.high}`}
          </p>
          <p className="font-mono2 text-[9px] uppercase tracking-widest text-zinc-500">crun credits ({est.model} · {est.resolution})</p>
        </div>
        <div>
          <p className={`text-2xl font-display ${short ? "text-red-400" : "text-emerald-400"}`} data-testid="est-balance">
            {bal != null ? Math.round(bal) : "—"}
          </p>
          <p className="font-mono2 text-[9px] uppercase tracking-widest text-zinc-500">credits left</p>
        </div>
      </div>
      {est.elevenlabs && (
        <p className="mt-3 text-[11px] text-zinc-500" data-testid="est-elevenlabs">
          ElevenLabs voiceover: {est.narration_chars.toLocaleString()} chars
          {est.elevenlabs.remaining != null ? ` · ${Number(est.elevenlabs.remaining).toLocaleString()} left` : ""}
          {est.elevenlabs.enough === false ? " — not enough characters!" : ""}
        </p>
      )}
      {short && (
        <p className="mt-2 flex items-center gap-1.5 text-[11px] text-red-400" data-testid="est-short-warning">
          <AlertTriangle className="w-3.5 h-3.5" /> Projected credits exceed your balance — top up on crun.ai before generating.
        </p>
      )}
      <p className="mt-2 text-[10px] text-zinc-600">Credits are crun.ai's unit (not dollars). Final billing is reconciled to actual usage. Music is cached/one-time.</p>
    </div>
  );
}

export default function AgentPanel({ project, onChange, onContinue }) {
  const [chars, setChars] = useState([]);
  const [name, setName] = useState("");
  const [desc, setDesc] = useState("");
  const [creating, setCreating] = useState(false);
  const [selecting, setSelecting] = useState(null);
  const [regenAgent, setRegenAgent] = useState(false);
  const [outro, setOutro] = useState(
    project.outro || { enabled: true, name: "", phone: "", brokerage: "", tagline: "Book your private showing today.", presented_by: "", logo_media_id: null }
  );
  const [savingOutro, setSavingOutro] = useState(false);

  const [intro, setIntro] = useState(
    project.intro || { enabled: false, style: "card", preset: "minimal", headline: "", subtitle: "", background_media_id: null, durationInSeconds: 3.5 }
  );
  const [savingIntro, setSavingIntro] = useState(false);
  const [suggesting, setSuggesting] = useState(false);

  const [onLoc, setOnLoc] = useState(!!project.settings?.agent_on_location);
  const [backdrop, setBackdrop] = useState(project.settings?.agent_backdrop_media_id || null);
  const [shot, setShot] = useState(project.settings?.agent_shot || "medium");
  const [photos, setPhotos] = useState([]);
  const [locPicker, setLocPicker] = useState(false);
  const [savingLoc, setSavingLoc] = useState(false);

  const [useAgent, setUseAgent] = useState(project.settings?.use_agent !== false);
  const [savingUseAgent, setSavingUseAgent] = useState(false);
  const toggleUseAgent = async () => {
    const next = !useAgent;
    setUseAgent(next);
    setSavingUseAgent(true);
    try {
      await api.post(`/projects/${project.id}/settings`, { use_agent: next });
      toast.success(next
        ? "On-camera presenter enabled"
        : "No presenter — your reel will use property clips, voiceover & music");
      onChange && onChange();
    } catch (e) {
      setUseAgent(!next);
      toast.error("Could not update that setting");
    } finally {
      setSavingUseAgent(false);
    }
  };


  const [voices, setVoices] = useState([]);
  const [voiceName, setVoiceName] = useState(project.settings?.voice_name || "");
  const [previewingVoice, setPreviewingVoice] = useState(false);
  const [savingVoice, setSavingVoice] = useState(false);

  useEffect(() => {
    api.get("/projects/voices").then((r) => {
      const vs = r.data.voices || [];
      setVoices(vs);
      const keys = new Set(vs.map((v) => v.key));
      const stored = project.settings?.voice_name;
      setVoiceName(keys.has(stored) ? stored : (r.data.default || vs[0]?.key || ""));
    }).catch(() => {});
  }, [project.id]);

  const previewVoice = async () => {
    if (!voiceName) return;
    setPreviewingVoice(true);
    try {
      const res = await api.post("/projects/voices/preview",
        { voice: voiceName, text: "Welcome to this beautiful home. Let me show you around." },
        { responseType: "blob" });
      new Audio(URL.createObjectURL(res.data)).play();
    } catch (err) {
      toast.error("Preview failed");
    } finally {
      setPreviewingVoice(false);
    }
  };

  const saveVoice = async () => {
    if (!voiceName) return;
    setSavingVoice(true);
    try {
      await api.post(`/projects/${project.id}/revoice`, { voice_name: voiceName });
      toast.success("Narration voice saved — used when clips generate (existing narration re-records).");
      onChange && onChange();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || err.message);
    } finally {
      setSavingVoice(false);
    }
  };

  useEffect(() => {
    api.get(`/projects/${project.id}/listing`).then((r) => setPhotos(r.data.image_media_ids || [])).catch(() => {});
  }, [project.id]);

  const saveAgentLocation = async (patch = {}) => {
    const next = { enabled: onLoc, backdrop_media_id: backdrop, shot, ...patch };
    setSavingLoc(true);
    try {
      await api.put(`/projects/${project.id}/agent-location`, next);
      setOnLoc(next.enabled);
      setBackdrop(next.backdrop_media_id);
      setShot(next.shot);
      toast.success("On-location saved");
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Save failed");
    } finally {
      setSavingLoc(false);
    }
  };

  const saveIntro = async (patch = {}) => {
    const next = { ...intro, ...patch };
    setIntro(next);
    setSavingIntro(true);
    try {
      await api.put(`/projects/${project.id}/intro`, next);
      toast.success("Title saved");
      onChange && onChange();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || err.message);
    } finally {
      setSavingIntro(false);
    }
  };

  const suggestTitle = async () => {
    setSuggesting(true);
    try {
      const r = await api.post(`/projects/${project.id}/intro/suggest`);
      setIntro((p) => ({ ...p, headline: r.data.headline || p.headline, subtitle: r.data.subtitle || p.subtitle }));
      toast.success("AI title ready — tweak it, then Save");
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || err.message);
    } finally {
      setSuggesting(false);
    }
  };

  const uploadIntroBg = async (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    const fd = new FormData();
    fd.append("files", f);
    try {
      const up = await api.post("/media/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
      await saveIntro({ background_media_id: up.data.uploaded[0].media_id });
    } catch (err) {
      toast.error("Image upload failed");
    }
  };

  const saveOutro = async (patch = {}) => {
    const next = { ...outro, ...patch };
    setOutro(next);
    setSavingOutro(true);
    try {
      await api.put(`/projects/${project.id}/outro`, next);
      toast.success("Outro saved");
      onChange && onChange();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || err.message);
    } finally {
      setSavingOutro(false);
    }
  };

  const uploadLogo = async (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    const fd = new FormData();
    fd.append("files", f);
    try {
      const up = await api.post("/media/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
      await saveOutro({ logo_media_id: up.data.uploaded[0].media_id });
    } catch (err) {
      toast.error("Logo upload failed");
    }
  };

  const uploadBackdrop = async (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    setSavingLoc(true);
    const fd = new FormData();
    fd.append("files", f);
    try {
      const up = await api.post("/media/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
      const mid = up.data.uploaded[0].media_id;
      setLocPicker(false);
      await saveAgentLocation({ backdrop_media_id: mid });
      toast.success("Custom backdrop uploaded");
    } catch (err) {
      toast.error("Backdrop upload failed");
    } finally {
      setSavingLoc(false);
    }
  };

  const load = () => api.get("/characters", { params: { vertical: project.vertical || "real_estate" } }).then((r) => setChars(r.data)).catch(() => {});
  useEffect(() => {
    load();
    const t = setInterval(load, 6000);
    return () => clearInterval(t);
  }, [project.vertical]);

  const select = async (character_id) => {
    setSelecting(character_id);
    try {
      await api.post(`/projects/${project.id}/character`, { character_id });
      toast.success("Agent cast");
      onChange && onChange();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || err.message);
    } finally {
      setSelecting(null);
    }
  };

  const regenerateAgentIntro = async () => {
    setRegenAgent(true);
    try {
      await api.post(`/projects/${project.id}/agent/regenerate`);
      toast.success("Updating the avatar on this reel — regenerating the intro…");
      onChange && onChange();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Update failed");
    } finally {
      setRegenAgent(false);
    }
  };

  const createCustom = async () => {
    if (!name || !desc) return toast.error("Name and description required");
    setCreating(true);
    try {
      await api.post("/characters", { name, description: desc, aspect: project.settings?.aspect || "9:16", vertical: project.vertical || "real_estate" });
      toast.success("Generating agent portrait…");
      setName(""); setDesc("");
      load();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || err.message);
    } finally {
      setCreating(false);
    }
  };

  const portrait = (c) =>
    c.portrait_media_id ? mediaUrl(c.portrait_media_id) : c.portrait_url || "";

  return (
    <div className="fade-up" data-testid="agent-panel">
      <EstimateBar projectId={project.id} />
      <div className="flex items-center gap-2 text-amber-500 mb-4">
        <Sparkles className="w-4 h-4" />
        <span className="font-mono2 text-[10px] uppercase tracking-[0.2em]">Cast a Virtual Agent</span>
      </div>

      <div className="mb-6 border border-zinc-800 bg-zinc-900/50 rounded-sm p-4 flex items-start justify-between gap-4" data-testid="use-agent-toggle-row">
        <div>
          <h4 className="font-display text-base font-medium text-zinc-100">On-camera presenter</h4>
          <p className="text-xs text-zinc-500 mt-1 max-w-md">
            {useAgent
              ? "An avatar introduces the reel on camera. Turn this off for a clean, agent-free reel — just property clips, voiceover, music and any info slides."
              : "No presenter. Your reel = property clips + voiceover + music + info slides. You can skip straight to generating clips."}
          </p>
        </div>
        <button
          data-testid="use-agent-toggle"
          onClick={toggleUseAgent}
          disabled={savingUseAgent}
          className={`shrink-0 font-mono2 text-[10px] uppercase tracking-widest px-3 py-1.5 rounded-sm border transition-colors ${useAgent ? "border-amber-500 text-amber-400 bg-amber-500/10" : "border-zinc-700 text-zinc-400"}`}
        >
          {savingUseAgent ? "…" : useAgent ? "Avatar: ON" : "Avatar: OFF"}
        </button>
      </div>

      {project.settings?.voice !== false && (
        <div className="mb-6 border border-zinc-800 bg-zinc-900/50 rounded-sm p-4" data-testid="agent-voice-picker">
          <p className="font-mono2 text-[9px] uppercase tracking-widest text-zinc-500 mb-2">Narration Voice (ElevenLabs)</p>
          <div className="flex items-center gap-2 flex-wrap">
            <select
              data-testid="agent-voice-select"
              value={voiceName}
              onChange={(e) => setVoiceName(e.target.value)}
              className="flex-1 min-w-[220px] h-10 rounded-sm bg-zinc-950 border border-zinc-800 text-sm text-zinc-200 px-3 focus:border-amber-500 outline-none"
            >
              {voices.length === 0 && <option value="">Loading voices…</option>}
              {voices.map((v) => (
                <option key={v.key} value={v.key}>{v.name}{v.desc ? ` — ${v.desc}` : ""}</option>
              ))}
            </select>
            <Button
              data-testid="agent-voice-preview"
              onClick={previewVoice}
              disabled={previewingVoice || !voiceName}
              className="h-10 rounded-sm bg-zinc-800 text-zinc-100 hover:bg-zinc-700 disabled:opacity-50"
            >
              {previewingVoice ? "Playing…" : "▶ Preview"}
            </Button>
            <Button
              data-testid="agent-voice-save"
              onClick={saveVoice}
              disabled={savingVoice || !voiceName}
              className="h-10 rounded-sm bg-amber-500 text-zinc-950 font-semibold hover:bg-amber-400 disabled:opacity-50"
            >
              {savingVoice ? <Loader2 className="w-4 h-4 animate-spin" /> : "Save voice"}
            </Button>
          </div>
          <p className="text-[11px] text-zinc-600 mt-2">This narration voice is used when clips generate. If clips already exist, saving re-records them in the new voice.</p>
        </div>
      )}

      {project.status === "pick_agent" && (
        <div className="mb-4 flex flex-col sm:flex-row sm:items-center gap-3 border border-amber-500/40 bg-amber-500/10 rounded-sm p-4" data-testid="pick-agent-banner">
          <p className="text-sm text-amber-200 flex-1">
            {!useAgent
              ? "No presenter selected — you're all set. Continue to generate the scene clips."
              : project.character_id
                ? "Agent cast. Continue to generate the scene clips."
                : "Pick an agent below (or generate a custom one) to introduce this property — or turn the presenter off above."}
          </p>
          <Button
            data-testid="agent-continue-button"
            onClick={() => onContinue && onContinue()}
            disabled={useAgent && !project.character_id}
            className="h-10 rounded-sm bg-amber-500 text-zinc-950 font-semibold hover:bg-amber-400 transition-colors disabled:opacity-50"
          >
            Continue → Generate Clips
          </Button>
        </div>
      )}

      {useAgent && (<>
      <div className="grid grid-cols-2 sm:grid-cols-3 gap-4">
        {chars.map((c) => {
          const selected = project.character_id === c.id;
          return (
            <button
              key={c.id}
              data-testid={`agent-card-${c.id}`}
              onClick={() => select(c.id)}
              disabled={selecting === c.id || c.status === "generating"}
              className={`group relative overflow-hidden rounded-sm border text-left transition-colors ${
                selected ? "border-amber-500" : "border-zinc-800 hover:border-zinc-600"
              }`}
            >
              <div className="aspect-[3/4] overflow-hidden bg-zinc-900">
                {portrait(c) ? (
                  <img src={portrait(c)} alt={c.name} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" />
                ) : (
                  <div className="w-full h-full flex items-center justify-center">
                    {c.status === "generating" ? (
                      <Loader2 className="w-5 h-5 text-amber-500 animate-spin" />
                    ) : (
                      <UserPlus className="w-6 h-6 text-zinc-700" />
                    )}
                  </div>
                )}
              </div>
              <div className="absolute inset-x-0 bottom-0 bg-gradient-to-t from-black/90 to-transparent p-3">
                <p className="text-sm font-medium text-white">{c.name}</p>
              </div>
              {selected && (
                <span className="absolute top-2 right-2 w-6 h-6 rounded-full bg-amber-500 flex items-center justify-center">
                  <Check className="w-3.5 h-3.5 text-zinc-950" />
                </span>
              )}
            </button>
          );
        })}
      </div>

      {project.character_id && project.status !== "pick_agent" && project.status !== "created" && (
        <div className="mt-6 border border-amber-500/40 bg-amber-500/5 rounded-sm p-5 flex flex-col sm:flex-row sm:items-center gap-3" data-testid="update-avatar-editor">
          <div className="flex-1">
            <h4 className="font-display text-lg font-medium">Update the avatar on this reel</h4>
            <p className="text-sm text-zinc-500 mt-1">
              Cast a new agent above (and, if you want them standing at the property, enable <span className="text-amber-400">Place agent on-location</span> and pick a backdrop). Then apply — we regenerate only the intro clip and re-render, keeping every other clip.
            </p>
          </div>
          <Button
            data-testid="update-avatar-button"
            onClick={regenerateAgentIntro}
            disabled={regenAgent}
            className="h-10 rounded-sm bg-amber-500 text-zinc-950 font-semibold hover:bg-amber-400 transition-colors whitespace-nowrap"
          >
            {regenAgent ? <Loader2 className="w-4 h-4 animate-spin" /> : <><RefreshCw className="w-4 h-4 mr-2" /> Update avatar on reel</>}
          </Button>
        </div>
      )}

      <div className="mt-6 bg-zinc-900 border border-zinc-800 rounded-sm p-6" data-testid="agent-location-editor">
        <div className="flex items-center justify-between mb-1">
          <h4 className="font-display text-lg font-medium">Place agent on-location</h4>
          <button
            data-testid="agent-location-toggle"
            onClick={() => saveAgentLocation({ enabled: !onLoc })}
            disabled={savingLoc}
            className={`font-mono2 text-[10px] uppercase tracking-widest px-3 py-1.5 rounded-sm border transition-colors ${onLoc ? "border-amber-500 text-amber-400 bg-amber-500/10" : "border-zinc-700 text-zinc-500"}`}
          >
            {savingLoc ? "…" : onLoc ? "Enabled" : "Disabled"}
          </button>
        </div>
        <p className="text-sm text-zinc-500 mb-4">
          Composites your agent standing at the property (on the deck, out front, etc.) so the talking intro looks shot on location — and fills a widescreen frame.
        </p>
        {onLoc && (
          <div>
            <p className="text-xs text-zinc-400 mb-2">Backdrop photo {backdrop ? "" : "— defaults to the first listing photo"}</p>
            <div className="flex items-center gap-3">
              {backdrop && <img src={mediaUrl(backdrop)} alt="" className="w-28 h-16 object-cover rounded-sm border border-amber-500/50" />}
              <button data-testid="agent-backdrop-pick" onClick={() => setLocPicker(!locPicker)} className="text-xs px-3 py-2 rounded-sm border border-zinc-700 text-zinc-300 hover:border-amber-500/60">
                {backdrop ? "Change backdrop" : "Choose backdrop"}
              </button>
              <label data-testid="agent-backdrop-upload" className="text-xs px-3 py-2 rounded-sm border border-zinc-700 text-zinc-300 hover:border-amber-500/60 cursor-pointer">
                {savingLoc ? "Uploading…" : "Upload your own"}
                <input type="file" accept="image/*" className="hidden" onChange={uploadBackdrop} disabled={savingLoc} />
              </label>
            </div>
            <p className="mt-2 text-[11px] text-zinc-500">
              Upload your own clean background (e.g. a photo with no people) to place the agent on it.
            </p>
            {locPicker && (
              <div className="mt-3 grid grid-cols-4 sm:grid-cols-6 gap-2 max-h-56 overflow-y-auto p-1" data-testid="agent-backdrop-grid">
                {photos.map((id) => (
                  <button key={id} data-testid={`agent-backdrop-${id}`}
                    onClick={() => { setLocPicker(false); saveAgentLocation({ backdrop_media_id: id }); }}
                    className={`aspect-video rounded-sm overflow-hidden border-2 ${backdrop === id ? "border-amber-500" : "border-transparent hover:border-zinc-600"}`}>
                    <img src={mediaUrl(id)} alt="" className="w-full h-full object-cover" />
                  </button>
                ))}
              </div>
            )}
            <div className="mt-4" data-testid="agent-shot-picker">
              <p className="text-xs text-zinc-400 mb-2">Agent framing</p>
              <div className="flex gap-2">
                {[
                  { v: "medium", label: "Medium", hint: "waist up — best face" },
                  { v: "three_quarter", label: "Three-quarter", hint: "mid-thigh up" },
                  { v: "full", label: "Full body", hint: "feet visible — smaller face" },
                ].map((o) => (
                  <button
                    key={o.v}
                    data-testid={`agent-shot-${o.v}`}
                    onClick={() => saveAgentLocation({ shot: o.v })}
                    className={`flex-1 text-left px-3 py-2 rounded-sm border transition-colors ${
                      shot === o.v
                        ? "border-amber-500 bg-amber-500/10 text-amber-400"
                        : "border-zinc-700 text-zinc-400 hover:border-zinc-600"
                    }`}
                  >
                    <span className="block text-sm font-medium">{o.label}</span>
                    <span className="block text-[10px] text-zinc-500">{o.hint}</span>
                  </button>
                ))}
              </div>
              <p className="mt-2 text-[11px] text-zinc-600">Applies next time you regenerate the agent intro. <span className="text-zinc-500">Medium/three-quarter give the sharpest, truest face — full-body makes the face small.</span></p>
            </div>
          </div>
        )}
      </div>

      <div className="mt-8 bg-zinc-900 border border-zinc-800 rounded-sm p-6">
        <h4 className="font-display text-lg font-medium mb-1">Generate a custom agent</h4>
        <p className="text-sm text-zinc-500 mb-4">Describe an agent and we'll create their portrait with Nano Banana Pro.</p>
        <Input data-testid="custom-agent-name" value={name} onChange={(e) => setName(e.target.value)} placeholder="Name (e.g. Jordan Lee)" className="bg-zinc-950 border-zinc-800 rounded-sm mb-3" />
        <Textarea data-testid="custom-agent-desc" value={desc} onChange={(e) => setDesc(e.target.value)} placeholder="a friendly male agent in a grey suit, short beard, 30s..." className="bg-zinc-950 border-zinc-800 rounded-sm resize-none" />
        <Button data-testid="create-agent-button" onClick={createCustom} disabled={creating} className="mt-4 h-10 rounded-sm bg-amber-500 text-zinc-950 font-semibold hover:bg-amber-400 transition-colors">
          {creating ? <Loader2 className="w-4 h-4 animate-spin" /> : <><UserPlus className="w-4 h-4 mr-2" /> Generate Agent</>}
        </Button>
      </div>
      </>)}

      <div className="mt-6 bg-zinc-900 border border-zinc-800 rounded-sm p-6" data-testid="intro-editor">
        <div className="flex items-center justify-between mb-1">
          <h4 className="font-display text-lg font-medium">Title / intro</h4>
          <button
            data-testid="intro-toggle"
            onClick={() => saveIntro({ enabled: !intro.enabled })}
            className={`font-mono2 text-[10px] uppercase tracking-widest px-3 py-1.5 rounded-sm border transition-colors ${
              intro.enabled ? "border-amber-500 text-amber-400 bg-amber-500/10" : "border-zinc-700 text-zinc-500"
            }`}
          >
            {intro.enabled ? "Enabled" : "Disabled"}
          </button>
        </div>
        <p className="text-sm text-zinc-500 mb-4">Opens the reel with a title — a full card before the clips, or an overlay on the first shot.</p>
        <div className="flex items-center gap-2 mb-3">
          {["card", "overlay"].map((st) => (
            <button
              key={st}
              data-testid={`intro-style-${st}`}
              onClick={() => setIntro({ ...intro, style: st })}
              className={`text-xs px-3 py-1.5 rounded-sm border capitalize transition-colors ${
                intro.style === st ? "border-amber-500 text-amber-400 bg-amber-500/10" : "border-zinc-700 text-zinc-400"
              }`}
            >
              {st === "card" ? "Title card" : "Overlay on first clip"}
            </button>
          ))}
        </div>
        {intro.style === "card" && (
          <div className="flex items-center gap-2 mb-3" data-testid="intro-preset-row">
            <span className="font-mono2 text-[9px] uppercase tracking-widest text-zinc-500 mr-1">Look</span>
            {[{ v: "minimal", l: "Minimal" }, { v: "bold", l: "Bold" }, { v: "photo", l: "Photo-backed" }].map((p) => (
              <button
                key={p.v}
                data-testid={`intro-preset-${p.v}`}
                onClick={() => setIntro({ ...intro, preset: p.v })}
                className={`text-xs px-3 py-1.5 rounded-sm border transition-colors ${
                  (intro.preset || "minimal") === p.v ? "border-amber-500 text-amber-400 bg-amber-500/10" : "border-zinc-700 text-zinc-400"
                }`}
              >
                {p.l}
              </button>
            ))}
          </div>
        )}
        <div className="grid sm:grid-cols-2 gap-3">
          <Input data-testid="intro-headline" value={intro.headline} onChange={(e) => setIntro({ ...intro, headline: e.target.value })} placeholder="Headline (e.g. West Springs Landing)" className="bg-zinc-950 border-zinc-800 rounded-sm" />
          <Input data-testid="intro-subtitle" value={intro.subtitle} onChange={(e) => setIntro({ ...intro, subtitle: e.target.value })} placeholder="Subtitle / tagline" className="bg-zinc-950 border-zinc-800 rounded-sm" />
        </div>
        <div className="flex flex-wrap items-center gap-3 mt-4">
          <Button data-testid="intro-suggest-button" onClick={suggestTitle} disabled={suggesting} variant="outline" className="h-10 rounded-sm border-zinc-700 text-zinc-200 hover:bg-zinc-800">
            {suggesting ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Sparkles className="w-4 h-4 mr-2 text-amber-400" />} Write it for me
          </Button>
          {intro.style === "card" && (
            <label className="flex items-center gap-2 border border-dashed border-zinc-700 rounded-sm px-4 py-2 cursor-pointer hover:border-amber-500/60 transition-colors text-sm text-zinc-400">
              <UserPlus className="w-4 h-4 text-amber-500" />
              {intro.background_media_id ? "Background added" : "Background photo (optional)"}
              <input data-testid="intro-bg-input" type="file" accept="image/*" className="hidden" onChange={uploadIntroBg} />
            </label>
          )}
          <Button data-testid="save-intro-button" onClick={() => saveIntro()} disabled={savingIntro} className="h-10 rounded-sm bg-amber-500 text-zinc-950 font-semibold hover:bg-amber-400 transition-colors">
            {savingIntro ? <Loader2 className="w-4 h-4 animate-spin" /> : "Save Title"}
          </Button>
          {intro.background_media_id && intro.style === "card" && (
            <img src={mediaUrl(intro.background_media_id)} alt="intro bg" className="h-10 w-16 object-cover rounded-sm" />
          )}
        </div>
      </div>

      <div className="mt-6 bg-zinc-900 border border-zinc-800 rounded-sm p-6" data-testid="outro-editor">
        <div className="flex items-center justify-between mb-1">
          <h4 className="font-display text-lg font-medium">Branded outro card</h4>
          <button
            data-testid="outro-toggle"
            onClick={() => saveOutro({ enabled: !outro.enabled })}
            className={`font-mono2 text-[10px] uppercase tracking-widest px-3 py-1.5 rounded-sm border transition-colors ${
              outro.enabled ? "border-amber-500 text-amber-400 bg-amber-500/10" : "border-zinc-700 text-zinc-500"
            }`}
          >
            {outro.enabled ? "Enabled" : "Disabled"}
          </button>
        </div>
        <p className="text-sm text-zinc-500 mb-4">Closes every reel with your call-to-action.</p>
        <div className="grid sm:grid-cols-2 gap-3">
          <Input data-testid="outro-name" value={outro.name} onChange={(e) => setOutro({ ...outro, name: e.target.value })} placeholder="Agent name" className="bg-zinc-950 border-zinc-800 rounded-sm" />
          <Input data-testid="outro-phone" value={outro.phone} onChange={(e) => setOutro({ ...outro, phone: e.target.value })} placeholder="Phone" className="bg-zinc-950 border-zinc-800 rounded-sm" />
          <Input data-testid="outro-brokerage" value={outro.brokerage} onChange={(e) => setOutro({ ...outro, brokerage: e.target.value })} placeholder="Brokerage" className="bg-zinc-950 border-zinc-800 rounded-sm" />
          <Input data-testid="outro-tagline" value={outro.tagline} onChange={(e) => setOutro({ ...outro, tagline: e.target.value })} placeholder="Tagline" className="bg-zinc-950 border-zinc-800 rounded-sm" />
        </div>
        <div className="mt-3">
          <label className="text-xs text-zinc-500 mb-1.5 block">Presented by (optional) — shown at the bottom of the outro</label>
          <Textarea
            data-testid="outro-presented-by"
            value={outro.presented_by || ""}
            onChange={(e) => setOutro({ ...outro, presented_by: e.target.value })}
            placeholder={"Rodeo Cut Studio\nrodeocutstudio.com"}
            rows={2}
            className="bg-zinc-950 border-zinc-800 rounded-sm resize-none"
          />
        </div>
        <div className="flex items-center gap-3 mt-4">
          <label className="flex items-center gap-2 border border-dashed border-zinc-700 rounded-sm px-4 py-2 cursor-pointer hover:border-amber-500/60 transition-colors text-sm text-zinc-400">
            <UserPlus className="w-4 h-4 text-amber-500" />
            {outro.logo_media_id ? "Logo added" : "Upload logo"}
            <input data-testid="outro-logo-input" type="file" accept="image/*" className="hidden" onChange={uploadLogo} />
          </label>
          <Button data-testid="save-outro-button" onClick={() => saveOutro()} disabled={savingOutro} className="h-10 rounded-sm bg-amber-500 text-zinc-950 font-semibold hover:bg-amber-400 transition-colors">
            {savingOutro ? <Loader2 className="w-4 h-4 animate-spin" /> : "Save Outro"}
          </Button>
          {outro.logo_media_id && (
            <img src={mediaUrl(outro.logo_media_id)} alt="logo" className="h-10 object-contain" />
          )}
        </div>
      </div>
    </div>
  );
}
